// ============================================================================
// Time & Labor — every shift that was removed, across everybody.
//
// ⛔ Ben chose BOTH places (2026-09-22): a person's removed shifts sit behind
// one line at the bottom of their punch card, and this tab holds all of them.
// It is deliberately the SAME component reading the SAME view — this page is
// the punch card's list with no worker filter, so the two can never drift
// apart or disagree about what was removed.
//
// ⛔ NOTHING IS WRITTEN HERE. It is a record, not a workflow: removing happens
// on the punch card where the shift is, and this page only reads back who did
// it, when and why. The view is security_invoker, so an account manager sees
// removals at their own buildings and nowhere else.
// ============================================================================

import { useMemo, useState } from 'react';
import { Button, PageHeader, TabBar } from '../../shared/components';
import { RemovedTable } from './console-parts';
import { useTimeTabsNav } from './time-console';
import { WorkerPicker } from './worker-picker';
import { useFacilityOptions, useRemovedSegments, useWorkerPicker } from './hooks/use-time-data';

export function TimeRemoved() {
  const tabsNav = useTimeTabsNav('removed');
  const [workerId, setWorkerId] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const filter = useMemo(
    () => ({ workerId: workerId || undefined, facilityId: facilityId || undefined }),
    [workerId, facilityId],
  );
  const { rows, loading, error, reload } = useRemovedSegments(filter);
  const workers = useWorkerPicker();
  const facilities = useFacilityOptions();

  const hours = rows.reduce((sum, r) => sum + (r.hours_removed ?? 0), 0);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow="Time & Labor"
        title="Removed shifts"
        meta={loading
          ? 'Loading…'
          : `${rows.length} removed · ${hours.toFixed(2)} h taken off the books · removing happens on Timecards, this is the record of it`}
        actions={<Button size="sm" variant="secondary" onClick={() => void reload()}>Refresh</Button>}
      />
      <div className="mt-3"><TabBar {...tabsNav} /></div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="min-w-[240px] flex-1 sm:max-w-xs">
          <WorkerPicker
            rows={workers.rows}
            value={workerId || null}
            onChange={setWorkerId}
            placeholder="All workers — type a name…"
            allowClear
          />
        </div>
        <select
          value={facilityId}
          onChange={(e) => setFacilityId(e.target.value)}
          className="h-9 max-w-xs rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm text-tcs-text"
          aria-label="Filter by facility"
        >
          <option value="">All facilities</option>
          {facilities.rows.map((f) => (
            <option key={f.facility_id} value={f.facility_id}>{f.display}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-st-danger-border bg-st-danger-bg px-4 py-3 text-sm text-st-danger-fg">
          {error}
        </div>
      )}

      <div className="mt-4">
        <RemovedTable rows={rows} showWorker={workerId === ''} />
      </div>

      <p className="mt-3 text-xs text-tcs-text-muted">
        A removed shift never counts toward anybody&rsquo;s hours or pay, and its punches stay on the
        permanent record forever. Nothing here can be edited.
      </p>
    </div>
  );
}
